import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, ColorToken, ContentNugget } from '@backrs_ui/common-ui';

import {
  getNuggetImageUrl,
  NUGGET_VARIANT_ICON_CONFIG,
} from '@backrs_ui/common-ui/src/molecules/Nugget/utils/nuggetImages.util';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { NuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetChip';

import { Box, SxProps, Theme } from '@mui/material';

type ContentNuggetChipProps = Readonly<{
  id?: string;
  nugget: ContentNugget;
  onRemove?: () => void;
  onClick?: () => void;
}>;

export const ContentNuggetChip = ({ id, nugget, onRemove, onClick }: ContentNuggetChipProps) => {
  const navigate = useNavigate();

  const renderChipIcon = (): ReactElement | undefined => {
    const variantConfig = NUGGET_VARIANT_ICON_CONFIG[nugget.variant];

    if (!variantConfig) {
      return undefined;
    }

    return (
      <Box sx={variantIconSx(variantConfig.bgColor)}>
        <AppIcon iconName={variantConfig.icon} diameter="12px" color={variantConfig.iconColor} />
      </Box>
    );
  };

  const chipIcon = renderChipIcon();
  const imageResult = getNuggetImageUrl({ nugget });
  // a tool shows the banner image from its card, not its sticker
  const toolBanner = nugget.tool_info?.banner_media?.media_url;
  const fallbackImage = imageResult?.type === 'URL' ? imageResult.url : undefined;
  // a variant with its own icon config keeps it in place of any image
  const imageUrl = chipIcon ? undefined : (toolBanner ?? fallbackImage);

  return (
    <NuggetChip
      id={id}
      label={nugget.title}
      image={imageUrl ? { src: imageUrl, alt: nugget.title || '' } : undefined}
      icon={chipIcon}
      onClick={onClick ?? (() => handlePanelNuggetClick(nugget, navigate))}
      onRemove={onRemove}
      sx={labelTruncationSx}
    />
  );
};

const variantIconSx =
  (bgColor: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: backrs.colors[bgColor],
  });

// a long title truncates rather than widening the chip past its row
const labelTruncationSx: SxProps<Theme> = {
  '& .MuiChip-label': {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
